import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SingleEventImageSlider from "./SingleEventImageSlider";
import SingleEventTitle from "./SingleEventTitle";
import BackToListButton from "../backtolistbutton/BackToListButton";
import MainGame from "../person-single/MainGame";
import ShareSNS from "../../common/sns/ShareSNS";
import { SonicTermData } from "../../common/classes/SonicTerm";
import ContentReportModalContainer from "../../common/report/ContentReportModalContainer";
import ContentReportButton from "../../common/report/ContentReportButton";
import ShareModal from "../../common/sns/ShareModal";
import { getTimestampToday } from "../../utils/GeneralUtils";
import { PageTypes } from "../../utils/PageTypes";
import styles from "./singleevent.module.scss";
import { JikeiDebutBadgeSVG } from "../../assets/img/JikeiDebutBadgeSVG";
import PageNotificationBanner from "../../common/PageNotificationBanner";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  type?: SonicTermData;
  name?: string;
  start_date?: number;
  end_date?: number;
  start_time?: number;
  end_time?: number;
  location?: string;
  img?: Array<string>;
  game?: Array<SonicTermData>;
  media?: Array<{ name?: string; url?: string }>;
  address?: string;
  direction?: string;
  map?: string;
  relatedLink?: Array<{ name?: string; url?: string }>;
  overview?: string;
  tags?: Array<string>;
  showJikeiBadge: boolean;
  relatedContents?: React.ReactNode;
};

const SingleEvent: React.FC<Props> = ({
  type,
  name,
  start_date,
  end_date,
  start_time,
  end_time,
  location,
  img,
  game,
  media,
  address,
  direction,
  map,
  relatedLink,
  overview,
  tags,
  showJikeiBadge,
  relatedContents,
}) => {
  const { t } = useTranslation();

  const [isShareModalShown, setIsShareModalShown] = useState<boolean>(false);
  const [isReportModalShown, setIsReportModalShown] = useState<boolean>(false);

  return (
    <>
      <main>
        <article className={styles["single-event-bg"]}>
          <div className={styles["single-event"]}>
            <div>
              <div className={styles["single-event__head"]}>
                {end_date && end_date < getTimestampToday() ? (
                  <PageNotificationBanner text="このイベントは終了しました" />
                ) : null}
                {showJikeiBadge ? (
                  <div className={styles["single-event__head-jikei"]}>
                    <div>
                      <JikeiDebutBadgeSVG />
                    </div>
                  </div>
                ) : null}
                {name ? (
                  <div className={styles["single-event__head-title"]}>
                    <SingleEventTitle
                      start_date={start_date}
                      end_date={end_date}
                      start_time={start_time}
                      end_time={end_time}
                    />
                    <div className={styles["single-event__modal-trigger-area"]}>
                      <ContentReportButton
                        handler={() => setIsReportModalShown(true)}
                      />

                      <button
                        className={styles["single-event__share-trigger-btn"]}
                        onClick={() => setIsShareModalShown(true)}
                      >
                        <FontAwesomeIcon icon={["far", "share-nodes"]} />
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className={styles["single-event__head-slider"]}>
                  <SingleEventImageSlider img={img} />
                </div>
                <h1 className={styles["single-event__title"]}>{name}</h1>
                <div className={styles["single-event__tags-area"]}>
                  {tags && tags.length > 0
                    ? tags.map((tag, i) => (
                        <SonicLink
                          to={`/event/?s=${tag}`}
                          disabled={true}
                          key={i}
                        >
                          <span>{tag}</span>
                        </SonicLink>
                      ))
                    : null}
                </div>
                <div className={styles["single-event__info"]}>
                  {location ? (
                    <div className={styles["single-event__location"]}>
                      {t(`event.location.${location}`)}
                    </div>
                  ) : null}
                  {type ? (
                    <div className={styles["single-event__type"]}>
                      {t(`event.type.${type.slug}`)}
                    </div>
                  ) : null}
                </div>
              </div>
              {game && game.length > 0 ? (
                <div className={styles["single-event__item"]}>
                  <MainGame games={game} />
                </div>
              ) : null}
              {media && media.length > 0 ? (
                <div className={styles["single-event__item"]}>
                  <h2
                    className={styles["single-event__item-title"]}
                    data-text="STREAMING"
                  >
                    <span className={styles["single-event__item-title__text"]}>
                      配信情報
                    </span>
                  </h2>
                  {/* TODO タグ 
                配信情報がYouTube or Twitch の時に埋め込みで動画が表示できるか
                そのためにマイページの入力側にも調整が必要か調査してほしいです。
                */}
                  <div className={styles["single-event__item-grid"]}>
                    {media.map((el, i) => (
                      <a
                        href={el.url}
                        key={i}
                        className={styles["single-event__item-grid__item"]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div
                          className={
                            styles["single-event__item-grid__item-text"]
                          }
                        >
                          {el.name}
                        </div>
                        <div
                          className={
                            styles["single-event__item-grid__item-box"]
                          }
                        ></div>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}
              {address || direction || map ? (
                <div className={styles["single-event__item"]}>
                  <h2
                    className={styles["single-event__item-title"]}
                    data-text="ACCESS"
                  >
                    <span className={styles["single-event__item-title__text"]}>
                      アクセス
                    </span>
                  </h2>
                  {address ? (
                    <div
                      className={`${styles["single-event__item-text"]} ${styles["single-event__access-item"]}`}
                    >
                      <div className={styles["single-event__icon"]}>
                        <FontAwesomeIcon icon={["fal", "map-marked-alt"]} />
                      </div>
                      <div>{address}</div>
                    </div>
                  ) : null}
                  {direction ? (
                    <div
                      className={`${styles["single-event__item-text"]} ${styles["single-event__access-item"]}`}
                    >
                      <div className={styles["single-event__icon"]}>
                        <FontAwesomeIcon icon={["fas", "person-walking"]} />
                      </div>
                      <div>{direction}</div>
                    </div>
                  ) : null}
                  {map ? (
                    <div className={styles["single-event__item-grid"]}>
                      <a
                        href={map}
                        className={styles["single-event__item-grid__item"]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div
                          className={
                            styles["single-event__item-grid__item-text"]
                          }
                        >
                          MAP
                        </div>
                        <div
                          className={
                            styles["single-event__item-grid__item-box"]
                          }
                        ></div>
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {relatedLink && relatedLink.length > 0 ? (
                <div className={styles["single-event__item"]}>
                  <h2
                    className={`${styles["single-event__item-title"]} ${styles["five"]}`}
                    data-text="LINK"
                  >
                    <span className={styles["single-event__item-title__text"]}>
                      関連リンク
                    </span>
                  </h2>
                  <div className={styles["single-event__item-grid"]}>
                    {relatedLink.map((el, i) => (
                      <a
                        href={el.url}
                        key={i}
                        className={styles["single-event__item-grid__item"]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div
                          className={
                            styles["single-event__item-grid__item-text"]
                          }
                        >
                          {el.name}
                        </div>
                        <div
                          className={
                            styles["single-event__item-grid__item-box"]
                          }
                        ></div>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className={styles["single-event__item"]}>
                <h2
                  className={`${styles["single-event__item-title"]} ${styles["six"]}`}
                  data-text="OVERVIEW"
                >
                  <span className={styles["single-event__item-title__text"]}>
                    イベント概要
                  </span>
                </h2>
                <div
                  className={`${styles["single-event__item-text"]} ${styles["overview"]}`}
                >
                  {overview}
                </div>
              </div>
              <div className={styles["single-event__official-announcement"]}>
                掲載情報にはサイトの性質上、差異が発生する場合がございます。
                <br />
                ぜひ公式サイトなどの情報も併せてご確認ください。
              </div>
              <ShareSNS />
            </div>
          </div>

          <BackToListButton name="イベント" to="/event" max_width={960} />

          {relatedContents ? relatedContents : null}

          {/* TODO タグ イベント掲載依頼のリンク先はお問い合わせの予定です。*/}
          {/* <div className={styles["single-event__regist-banner"]}>
          <a href="">
            <img src="https://placehold.jp/d9265c/ffffff/960x195.png" alt="" />
          </a>
        </div> */}
        </article>
      </main>

      <ShareModal
        isModalShown={isShareModalShown}
        closeHandler={() => setIsShareModalShown(false)}
      />

      <ContentReportModalContainer
        isModalShown={isReportModalShown}
        closeHandler={() => setIsReportModalShown(false)}
        pageType={PageTypes.Event}
        contentName={name}
      />
    </>
  );
};

export default SingleEvent;
